import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomButton,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomMoneyInput,
  CustomNumberInput,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';

import {
  instructionDetailPriceUpdate,
  setTotalAutoLoading,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
  getTotalAutoLoadingParams,
  getDetailsPageList,
  setAutoLoadingNotIncludedCards,
} from '@src/store/slice/autoUploadInstructionsSlice';

import { isGreaterMaximumLoadedAmount } from '@src/utils/utils';

import { Form } from 'antd';

import '@src/styles/autoUploadInstruction/dailyOrder.scss';

const DailyOrder = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const {
    selectedAutoUploadDetails,
    totalAutoLoading,
    isAllSelectedRowKeys,
    integrationId,
    autoLoadingId,
    detailTableProperty,
    autoLoadingNotIncludedCards,
    autoUploadFilterItems,
  } = useSelector((state) => state?.autoUploadInstructions);
  const { maximumLoadedAmount } = useSelector((state) => state?.orderLimits);

  const [dayValidMessage, setDayValidMessage] = useState('');

  const maximumLoadingLimitMessage = useText('maximumLoadingLimitPerCardIs');

  useEffect(() => {
    if (totalAutoLoading) {
      form.setFieldsValue({
        dayCount: totalAutoLoading?.dayCount,
        dailyPrice: totalAutoLoading?.dailyPrice,
      });
    }
  }, []);

  const getAddressIdList = (autoUploadFilterItems) => {
    return Array.isArray(autoUploadFilterItems)
      ? autoUploadFilterItems
        .map((item) => {
          if (Array.isArray(item)) {
            return item.length > 0 ? { addressId: item[0] } : null;
          } else if (item && typeof item === 'object' && item.addressId) {
            return { addressId: item.addressId };
          }
          return null;
        })
        .filter((item) => item !== null)
      : null;
  };

  const standartUpdate = async () => {
    const addressIdList = getAddressIdList(autoUploadFilterItems);

    if (autoLoadingId && autoLoadingId > 0) {
      await dispatch(
        getDetailsPageList({
          page: detailTableProperty?.currentPage,
          pageSize: detailTableProperty?.pageSize,
          showOnlyPriced: false,
          autoLoadingId: totalAutoLoading?.id,
          addressIdList: addressIdList,
        }),
      );
      const body = {
        autoLoadingId: autoLoadingId,
        integrationId: 0,
      };
      await dispatch(getTotalAutoLoadingParams(body));
    }
  };

  const submitSelected = async () => {
    if (isAllSelectedRowKeys) {
      await submitAll();
      return;
    }
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.dayCount * values?.dailyPrice,
        })
      ) {
        const msg = maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount);
        errorDialog({
          title: <Text t="error" />,
          message: msg,
        });
        return;
      }

      if (selectedAutoUploadDetails.length > 0) {
        let data = {
          dayCount: values.dayCount,
          price: values.dailyPrice,
          cardNumbers: selectedAutoUploadDetails.map((item) => item.cardNumber),
          autoLoadingId: totalAutoLoading?.id,
        };

        const res = await dispatch(instructionDetailPriceUpdate(data));

        if (instructionDetailPriceUpdate.fulfilled.match(res)) {
          await dispatch(
            setTotalAutoLoading({
              ...totalAutoLoading,
              periodType: 0,
            }),
          );

          await standartUpdate();

          successDialog({
            title: <Text t="success" />,
            message: res?.payload.message || <Text t="successfullySent" />,
            onOk: async () => {
              await dispatch(setSelectedRowKeys([]));
              await dispatch(setIsAllSelectedRowKeys(false));
              await dispatch(setAutoLoadingNotIncludedCards([]));
            },
          });
        }
      } else {
        errorDialog({
          title: <Text t="error" />,
          htmlContent: <Text t="noSelectedRecord" />,
        });
      }
    } catch (e) { }
  };

  const submitAll = async () => {
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: values?.dayCount * values?.dailyPrice,
        })
      ) {
        const msg = maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount);
        errorDialog({
          title: <Text t="error" />,
          message: msg,
        });
        return;
      }

      const justCardNumberFilterItems = Array.isArray(autoUploadFilterItems)
        ? autoUploadFilterItems.map((item) => item.cardNumber)
        : [];

      const notSelectedCards = Array.isArray(autoLoadingNotIncludedCards)
        ? autoLoadingNotIncludedCards.map((item) => item.cardNumber)
        : [];

      let data = {
        dayCount: values.dayCount,
        price: values.dailyPrice,
        autoLoadingId: totalAutoLoading?.id,
        cardNumbers: autoUploadFilterItems?.length === 0 ? null : justCardNumberFilterItems,
        notIncludedCards:
          notSelectedCards?.length === 0 || !isAllSelectedRowKeys ? null : notSelectedCards,
      };

      const res = await dispatch(instructionDetailPriceUpdate(data));

      if (instructionDetailPriceUpdate.fulfilled.match(res)) {
        await dispatch(
          setTotalAutoLoading({
            ...totalAutoLoading,
            periodType: 0,
          }),
        );

        await standartUpdate();

        successDialog({
          title: <Text t="success" />,
          message: res?.payload.message || <Text t="successfullySent" />,
          onOk: async () => {
            await dispatch(setSelectedRowKeys([]));
            await dispatch(setIsAllSelectedRowKeys(false));
            await dispatch(setAutoLoadingNotIncludedCards([]));
          },
        });
      }
    } catch (e) { }
  };

  const dayCountValid = (field, value) => {
    if (value === 0) {
      setDayValidMessage('requiredField2');
      return Promise.reject(new Error('error'));
    }
    setDayValidMessage('');
    return Promise.resolve();
  };

  return (
    <CustomCollapseCard
      className="autoUploadInstruction-daily-order-card"
      cardTitle={<Text t="orderAmount2" />}
    >
      <div className="autoUploadInstruction-daily-order-container">
        <CustomForm
          name="dailyOrderForm"
          className="autoUploadInstruction-daily-order-form"
          autoComplete="off"
          layout={'horizontal'}
          form={form}
        >
          <div className="form-item">
            <div className="daily-count-content">
              <div className="daily-count-content-day-count">
                <p>
                  <Text t="numberOfDays" />
                </p>
              </div>
              <div className="daily-count-content-show">
                <CustomFormItem
                  name="dayCount"
                  className="daily-count-form-item"
                  rules={[
                    { required: true, message: <Text t="requiredField2" /> },
                    { validator: dayCountValid, message: <Text t={dayValidMessage} /> },
                  ]}
                >
                  <DayCount dayCount={totalAutoLoading.dayCount} />
                </CustomFormItem>
                <span className="info-text">
                  <Text t="day3" />
                </span>
              </div>
            </div>

            <div className="daily-amount-content">
              <div className="daily-amount-content-daily-price">
                <p>
                  <Text t="dailyAmount2" />
                </p>
              </div>
              <div className="daily-amount-content-show">
                <CustomFormItem
                  name="dailyPrice"
                  className="daily-amount-form-item"
                  rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                >
                  <DailyPrice
                    dailyPrice={totalAutoLoading.dailyPrice}
                    dayCount={form.getFieldsValue('dayCount')}
                  />
                </CustomFormItem>
                <span className="info-text">
                  <Text t="tlDayVatIncluded" />
                </span>
              </div>
            </div>
          </div>
        </CustomForm>

        <div className="action-buttons">
          <CustomButton
            id="ticketRestaurant-autoUploadInstructions-instructionInformations-dailyOrder-cancel-btn"
            className="cancel-btn"
            onClick={submitSelected}
          >
            <Text t="applyToSelected2" />
          </CustomButton>
          <CustomButton
            id="ticketRestaurant-autoUploadInstructions-instructionInformations-dailyOrder-primary-btn"
            className="primary-btn"
            onClick={submitAll}
          >
            <Text t="applyToAll2" />
          </CustomButton>
        </div>
      </div>
    </CustomCollapseCard>
  );
};

export default DailyOrder;

const DayCount = (props) => {
  const handleDailyInputChange = async (value) => {
    const dayCount = 99;
    if (value > dayCount) {
      props.onChange(props.dayCount);
    }
    props.onChange(value);
  };

  return (
    <CustomNumberInput
      {...props}
      id="ticketRestaurant-autoUploadInstructions-instructionInformations-dailyOrder-dayCount-numberInput"
      autoComplete="off"
      placeholder=""
      maxLength={2}
      height={36}
      max={10}
      onChange={handleDailyInputChange}
    />
  );
};

const DailyPrice = (props) => {
  const dispatch = useDispatch();

  const { totalAutoLoading } = useSelector((state) => state?.autoUploadInstructions);

  const handleDailyPrice = useCallback(
    async (value) => {
      dispatch(setTotalAutoLoading({ ...totalAutoLoading, dailyPrice: value }));
      props.onChange(value);
    },
    [totalAutoLoading],
  );

  return (
    <CustomMoneyInput
      {...props}
      id="ticketRestaurant-autoUploadInstructions-instructionInformations-dailyOrder-dailyPrice-numberInput"
      autoComplete="off"
      placeholder=""
      height={36}
      onChange={handleDailyPrice}
    />
  );
};
